import { useEffect, useState } from 'react';
import axios from '../../services/api-axios';
import { connectToLobby, setLobbiesList, removeLobbyFromList, LobbyInfo } from '../../store/lobbySlice';
import useLobbyMessageHandler from '../../message-handler/useLobbyMessageHandler';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { subscribe, subscribeWithPassword } from '../../services/ws-service';
import { leaveLobby, lobbyTopic } from '../../services/lobby-service';
import PasswordPromptModal from './PasswordPromptModal';
import styles from './LobbyTable.module.css';

const LobbyTable = () => {
    const onLobbyMessage = useLobbyMessageHandler();
    const dispatch = useAppDispatch();
    const lobbiesList = useAppSelector((state) => state.lobby.lobbiesList);
    const currentLobbyId = useAppSelector((state) => state.lobby.lobbyId);
    const username = useAppSelector((state) => state.onlineUsers.username);
    const [showPasswordModal, setShowPasswordModal] = useState(false);
    const [selectedLobby, setSelectedLobby] = useState<LobbyInfo | null>(null);

    useEffect(() => {
        axios
            .get('rest/lobby')
            .then((response) => {
                dispatch(setLobbiesList({ lobbies: response.data }));
            })
            .catch((error) => console.error(error));
    }, []);

    const handleJoinLobby = (lobby: LobbyInfo) => {
        if (lobby != null) {
            setSelectedLobby(lobby);
            lobby.passwordProtected ? setShowPasswordModal(true) : joinLobby(lobby, null);
        }
    };

    const handlePasswordSubmit = (password: string) => {
        if (selectedLobby) {
            joinLobby(selectedLobby, password);
        }
    };

    const joinLobby = async (lobby: LobbyInfo, password: string | null) => {
        if (lobby.lobbyId === currentLobbyId) return;

        // Has to happen before the join: a session bound to two lobbies at once can
        // be released from neither, and both are then stuck in the list forever.
        await leaveLobby(currentLobbyId, username);

        try {
            await axios.post('/rest/lobby/join/', {
                invitor: 'marjan',
                lobbyId: lobby.lobbyId,
            });
            if (password) {
                subscribeWithPassword(lobbyTopic(lobby.lobbyId), onLobbyMessage, password);
            } else {
                subscribe(lobbyTopic(lobby.lobbyId), onLobbyMessage);
            }
            dispatch(connectToLobby(lobby.lobbyId));
        } catch (error) {
            // A listed lobby that refuses joins no longer exists; stop offering it.
            console.error(error);
            dispatch(removeLobbyFromList({ lobbyId: lobby.lobbyId }));
        }
    };

    return (
        <div className={styles.panel}>
            <div className={styles.header}>
                <h3 className={styles.title}>🎮 Available Lobbies</h3>
                <div className={styles.countPill}>{lobbiesList.length} active lobbies</div>
            </div>

            {lobbiesList.length === 0 ? (
                <div className={styles.emptyState}>
                    <div className={styles.emptyIcon}>🎯</div>
                    <h4 className={styles.emptyTitle}>No Active Lobbies</h4>
                    <p>Be the first to create a lobby and start playing!</p>
                </div>
            ) : (
                <div className={styles.list}>
                    {lobbiesList.map((entry) => (
                        <div key={entry.lobbyId} className={styles.card}>
                            <div className={styles.cardTop}>
                                <div className={styles.cardIdentity}>
                                    <div className={`${styles.cardIcon} ${entry.passwordProtected ? styles.cardIconPrivate : styles.cardIconPublic}`}>
                                        {entry.passwordProtected ? '🔒' : '📢'}
                                    </div>
                                    <div>
                                        <h4 className={styles.cardName}>{entry.name}</h4>
                                        <div className={styles.cardMeta}>
                                            <span>👥 {entry.participantCount} players</span>
                                        </div>
                                    </div>
                                </div>

                                <button className={styles.joinButton} onClick={() => handleJoinLobby(entry)}>
                                    Join Lobby
                                </button>
                            </div>

                            <div className={styles.cardFooter}>
                                <div className={styles.activeIndicator}>
                                    <div className={styles.activeDot} />
                                    Active
                                </div>
                                <div className={`${styles.visibilityTag} ${entry.passwordProtected ? styles.visibilityTagPrivate : styles.visibilityTagPublic}`}>
                                    {entry.passwordProtected ? 'Private' : 'Public'}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <PasswordPromptModal
                show={showPasswordModal}
                onHide={() => setShowPasswordModal(false)}
                onPasswordSubmit={handlePasswordSubmit}
            />
        </div>
    );
};

export default LobbyTable;
